import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import I18n from 'i18n-js';
import cx from 'classnames';
import { Input } from '@duxton-web/ui';
import ModalDrawer from 'components/drawer/modal-drawer';
import scribeTrack from 'scribe/scribe-track';
import instrumentationDuck from 'ducks/instrumentation-duck';
import { inGrabApp } from 'common/utils/platform';
import staticMapPng from '../../public/images/static-map.png';
import styles from './add-instruction-drawer.less';

const TEXT_LIMIT = 200;
const { TextArea } = Input;

const AddInstructionDrawer = ({
  entry,
  isLoading,
  open,
  name,
  address,
  onClose,
  onSubmit,
}) => {
  const inputRef = useRef(null);
  const { orderId, trackingSource } = useSelector(
    instrumentationDuck.selectors.instrumentation,
  );
  const [text, setText] = useState('');
  const isOverTextLimit = text.length > TEXT_LIMIT;

  const messageClassName = cx(styles.message, {
    [styles.errorMessage]: isOverTextLimit,
  });

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onTextChange = e => {
    setText(e.target.value);
  };

  const innerOnClose = () => {
    scribeTrack.expressTrackingAddInstruction.close({
      trackingSource,
      addInstructionPosition: entry,
      orderIdDisplayed: orderId,
      isInApp: inGrabApp(),
    });
    setText('');
    onClose();
  };

  const innerOnSubmit = () => {
    scribeTrack.expressTrackingAddInstruction.send({
      trackingSource,
      addInstructionPosition: entry,
      orderIdDisplayed: orderId,
      isInApp: inGrabApp(),
    });

    onSubmit(text);
  };

  useEffect(
    () => {
      if (open) {
        scribeTrack.expressTrackingAddInstruction.default({
          trackingSource,
          addInstructionPosition: entry,
          orderIdDisplayed: orderId,
          isInApp: inGrabApp(),
        });
      }
    },
    [open],
  );

  const renderContent = () => (
    <div className={styles.instructionContent}>
      <div className={styles.locationInfo}>
        <img className={styles.image} src={staticMapPng} alt="static map" />

        <div className={styles.addressInfo}>
          <p className={styles.name}>{name}</p>
          <p className={styles.address}>{address}</p>
        </div>
      </div>

      <TextArea
        ref={inputRef}
        className={styles.input}
        value={text}
        onChange={onTextChange}
        rows={4}
        placeholder={I18n.t('deliveryDetails.instructions.inputPlaceholder')}
        disabled={isLoading}
        count={{
          show: true,
          max: TEXT_LIMIT,
        }}
        onTouchStart={e => {
          e.stopPropagation();
          handleFocus();
        }}
      />

      <p className={messageClassName}>
        {isOverTextLimit
          ? I18n.t('deliveryDetails.instructions.overTextLimit')
          : I18n.t('deliveryDetails.instructions.sendOnce')}
      </p>
    </div>
  );

  return (
    <ModalDrawer
      open={open}
      title={I18n.t('deliveryDetails.instructions.addDeliveryInstructions')}
      buttonText={I18n.t('deliveryDetails.instructions.sendToDriver')}
      disableButton={isOverTextLimit || isLoading}
      content={renderContent()}
      onClose={innerOnClose}
      onSubmit={innerOnSubmit}
    />
  );
};

AddInstructionDrawer.propTypes = {
  entry: PropTypes.string,
  isLoading: PropTypes.bool,
  open: PropTypes.bool,
  name: PropTypes.string,
  address: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

AddInstructionDrawer.defaultProps = {
  entry: '',
  isLoading: false,
  open: false,
  name: '',
  address: '',
  onClose: () => {},
  onSubmit: () => {},
};

export default AddInstructionDrawer;
